import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { PencilSimple, Trash, Target, Trophy, CheckCircle } from "@phosphor-icons/react";

const UNIT = { bookings: "bookings", rental_days: "days" };

/** One challenge with every affiliate's progress and standing. */
export default function ChallengeCard({ challenge: ch, admin = false, onEdit, onDelete, onSaveManual }) {
  const [scores, setScores] = useState(null);
  const unit = ch.metric === "manual" ? (ch.metric_label || "points") : UNIT[ch.metric];
  const window = [ch.start_date, ch.end_date].filter(Boolean).join(" → ") || "Ongoing";
  const standings = ch.standings || [];

  const startScores = () => setScores(Object.fromEntries(standings.map((s) => [s.name, s.value])));
  const save = async () => { await onSaveManual(scores); setScores(null); };

  return (
    <div className={`border bg-card p-6 ${ch.active === false ? "border-border opacity-70" : "border-primary/25"}`} data-testid={`challenge-card-${ch.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Target size={16} className="text-primary shrink-0" />
            <h3 className="font-heading text-xl truncate" data-testid={`challenge-title-${ch.id}`}>{ch.title}</h3>
            {ch.active === false && <span className="label-mono text-[10px] text-muted-foreground border border-border px-2 py-0.5">Paused</span>}
          </div>
          <div className="label-mono text-[10px] text-muted-foreground mt-1.5">
            {Number(ch.target) || 0} {unit} · {window}
            {ch.vehicle_class ? ` · ${ch.vehicle_class} only` : ""}
            {ch.booking_type ? ` · ${ch.booking_type} only` : ""}
          </div>
          {ch.description && <p className="text-sm text-muted-foreground mt-2 max-w-xl">{ch.description}</p>}
        </div>
        <div className="flex items-center gap-3">
          {ch.prize && (
            <div className="flex items-center gap-1.5 border border-primary/30 bg-primary/10 px-3 py-1.5" data-testid={`challenge-prize-${ch.id}`}>
              <Trophy size={13} className="text-primary" />
              <span className="text-sm">{ch.prize}</span>
            </div>
          )}
          {admin && (
            <div className="flex gap-1">
              {ch.metric === "manual" && (scores ? (
                <Button size="sm" onClick={save} data-testid={`challenge-save-scores-${ch.id}`}>Save scores</Button>
              ) : (
                <Button size="sm" variant="outline" onClick={startScores} data-testid={`challenge-enter-scores-${ch.id}`}>Enter scores</Button>
              ))}
              <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`challenge-edit-${ch.id}`}><PencilSimple size={14} className="text-primary" /></Button>
              <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`challenge-delete-${ch.id}`}><Trash size={14} className="text-red-400" /></Button>
            </div>
          )}
        </div>
      </div>

      {standings.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-5">No affiliates on the roster yet.</p>
      ) : (
        <ul className="mt-5 space-y-2" data-testid={`challenge-standings-${ch.id}`}>
          {standings.map((s) => (
            <li key={s.name} className="flex items-center gap-3" data-testid={`challenge-row-${ch.id}-${s.name}`}>
              <span className="w-6 label-mono text-xs text-muted-foreground shrink-0">#{s.rank}</span>
              <span className="w-40 truncate text-sm shrink-0">{s.name}</span>
              <div className="flex-1 h-2 bg-background border border-border overflow-hidden">
                <div
                  className={`h-full ${s.hit ? "bg-emerald-500" : "bg-primary"}`}
                  style={{ width: `${s.pct}%` }}
                />
              </div>
              {scores ? (
                <Input
                  type="number"
                  value={scores[s.name] ?? 0}
                  onChange={(e) => setScores({ ...scores, [s.name]: Number(e.target.value) })}
                  className="h-8 w-20 bg-input border-border text-right tnum"
                  data-testid={`challenge-score-input-${ch.id}-${s.name}`}
                />
              ) : (
                <span className="w-24 text-right text-sm tnum shrink-0" data-testid={`challenge-value-${ch.id}-${s.name}`}>
                  {s.value} / {Number(ch.target) || 0}
                </span>
              )}
              {s.hit && <CheckCircle size={15} className="text-emerald-500 shrink-0" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
